import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Skeleton,
  Alert,
  //   TextField,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { ArrowLeft } from '@phosphor-icons/react';
import LayoutWithSidebar from '../components/common/layoutWithSidebar';
import { HeaderContainer } from '../components/dashboard/AgentInfoHeader';
import { getPromptVersionHistoryApi } from '../utils/apis';
import { commonStyles } from '../utils/styles/inputs';
import theme from '../theme';

interface PromptField {
  key: keyof SystemPromptData;
  label: string;
  placeholder: string;
}

interface SystemPromptData {
  firstMessage: string;
  identity: string;
  context: string;
  job: string;
  examples: string;
  constraints: string;
  format: string;
  tone: string;
  version?: number;
  _id?: string;
  createdAt?: string;
  updatedAt?: string;
  label?: string[];
  commitMessage?: string;
}

// Use the same field order and labels as SystemPrompt.tsx
const promptFields: PromptField[] = [
  {
    key: 'identity',
    label: 'Identity',
    placeholder: 'What persona do you want this AI to take?',
  },
  {
    key: 'firstMessage',
    label: 'Greetings',
    placeholder: 'The Opening line of AI assistant to start the conversation',
  },
  {
    key: 'context',
    label: 'Context',
    placeholder: 'Provide information about the situation or environment',
  },
  {
    key: 'job',
    label: 'Job',
    placeholder: "What's the task you want accomplished?",
  },
  {
    key: 'examples',
    label: 'Examples',
    placeholder: "Any references you'd like the AI to be guided by",
  },
  {
    key: 'constraints',
    label: 'Constraints',
    placeholder: "Boundaries you'd like the AI to stay within or go beyond",
  },
  {
    key: 'format',
    label: 'Format',
    placeholder: 'Request the answer in a table, email, list, etc.',
  },
  {
    key: 'tone',
    label: 'Tone',
    placeholder:
      'Examples: Friendly, professional, authoritative, informative, urgent',
  },
];

const PromptCompare: React.FC = () => {
  const { agentId, versionId1, versionId2 } = useParams<{
    agentId: string;
    versionId1: string;
    versionId2: string;
  }>();
  const navigate = useNavigate();

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Fetch version history data
  const dateRange = useMemo(() => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 365); // Get a year's worth of data
    const toDate = new Date();

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };
  }, []);

  const { data: promptData, isLoading } = useQuery({
    queryKey: [
      'get-version-history-compare',
      agentId,
      dateRange.fromDate,
      dateRange.toDate,
    ],
    queryFn: () =>
      getPromptVersionHistoryApi({
        agentId: agentId || '',
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      }),
    enabled: !!agentId && !!versionId1 && !!versionId2,
  });

  // Find the specific versions to compare
  const versions = useMemo(() => {
    if (!promptData?.data?.data) return { version1: null, version2: null };

    const allVersions = promptData.data.data;
    const version1 = allVersions.find((v: any) => v._id === versionId1);
    const version2 = allVersions.find((v: any) => v._id === versionId2);

    return { version1, version2 };
  }, [promptData, versionId1, versionId2]);

  //   const formatTimeAgo = (dateString: string) => {
  //     const now = new Date();
  //     const date = new Date(dateString);
  //     const diffInMs = now.getTime() - date.getTime();
  //     const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  //     if (diffInDays === 0) return 'Today';
  //     if (diffInDays === 1) return '1 day ago';
  //     if (diffInDays < 30) return `${diffInDays} days ago`;

  //     const diffInMonths = Math.floor(diffInDays / 30);
  //     return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  //   };

  const isFieldDifferent = (field: keyof SystemPromptData) => {
    if (!versions.version1 || !versions.version2) return false;
    return versions.version1[field] !== versions.version2[field];
  };

  if (isLoading) {
    return (
      <LayoutWithSidebar margin={2}>
        <Box
          sx={{
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <HeaderContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={300} height={32} />
            </Box>
          </HeaderContainer>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2 }}>
                  <Skeleton
                    variant="text"
                    width={200}
                    height={30}
                    sx={{ mb: 2 }}
                  />
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width={100} height={20} />
                      <Skeleton variant="text" width="100%" height={60} />
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2 }}>
                  <Skeleton
                    variant="text"
                    width={200}
                    height={30}
                    sx={{ mb: 2 }}
                  />
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width={100} height={20} />
                      <Skeleton variant="text" width="100%" height={60} />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </LayoutWithSidebar>
    );
  }

  if (!versions.version1 || !versions.version2) {
    return (
      <LayoutWithSidebar margin={2}>
        <Box
          sx={{
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <HeaderContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handleBack} sx={{ padding: '8px' }}>
                <ArrowLeft
                  size={20}
                  color={theme.palette.custom.textBlue}
                  weight="bold"
                />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: theme.palette.custom.textBlue,
                }}
              >
                Prompt Version Comparison
              </Typography>
            </Box>
          </HeaderContainer>

          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              Unable to find the selected versions for comparison. Please try
              again.
            </Alert>
          </Box>
        </Box>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar margin={2}>
      {/* Header with Back Button - This will be fixed */}
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ padding: '8px' }}>
            <ArrowLeft
              size={20}
              color={theme.palette.custom.textBlue}
              weight="bold"
            />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: theme.palette.custom.textBlue,
            }}
          >
            Prompt Version Comparison
          </Typography>
        </Box>

        {/* Version Info in Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}
            >
              Comparing:
            </Typography>

            {/* Version 1 with Tooltip */}
            <Tooltip
              title={
                <Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>
                    Version {versions.version1.version}
                  </Typography>
                  {versions.version1.commitMessage && (
                    <Typography
                      sx={{ fontSize: '11px', mb: 1, fontStyle: 'italic' }}
                    >
                      "{versions.version1.commitMessage}"
                    </Typography>
                  )}
                  {versions.version1.label &&
                    versions.version1.label.length > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: '11px', mb: 0.5 }}>
                          Labels:
                        </Typography>
                        <Box
                          sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
                        >
                          {versions.version1.label.map(
                            (label: string, index: number) => (
                              <Chip
                                key={index}
                                label={label}
                                size="small"
                                sx={{
                                  height: '16px',
                                  fontSize: '9px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  color: 'inherit',
                                }}
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    )}
                  {!versions.version1.commitMessage &&
                    (!versions.version1.label ||
                      versions.version1.label.length === 0) && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontStyle: 'italic',
                          opacity: 0.7,
                        }}
                      >
                        No additional details
                      </Typography>
                    )}
                </Box>
              }
              placement="bottom"
              arrow
            >
              <Chip
                label={`Version ${versions.version1.version}`}
                size="small"
                sx={{
                  height: '24px',
                  fontSize: '12px',
                  backgroundColor: '#FFEBEE',
                  color: '#C62828',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#FFCDD2',
                  },
                }}
              />
            </Tooltip>

            <Typography sx={{ color: theme.palette.text.secondary }}>
              vs
            </Typography>

            {/* Version 2 with Tooltip */}
            <Tooltip
              title={
                <Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>
                    Version {versions.version2.version}
                  </Typography>
                  {versions.version2.commitMessage && (
                    <Typography
                      sx={{ fontSize: '11px', mb: 1, fontStyle: 'italic' }}
                    >
                      "{versions.version2.commitMessage}"
                    </Typography>
                  )}
                  {versions.version2.label &&
                    versions.version2.label.length > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: '11px', mb: 0.5 }}>
                          Labels:
                        </Typography>
                        <Box
                          sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
                        >
                          {versions.version2.label.map(
                            (label: string, index: number) => (
                              <Chip
                                key={index}
                                label={label}
                                size="small"
                                sx={{
                                  height: '16px',
                                  fontSize: '9px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  color: 'inherit',
                                }}
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    )}
                  {!versions.version2.commitMessage &&
                    (!versions.version2.label ||
                      versions.version2.label.length === 0) && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontStyle: 'italic',
                          opacity: 0.7,
                        }}
                      >
                        No additional details
                      </Typography>
                    )}
                </Box>
              }
              placement="bottom"
              arrow
            >
              <Chip
                label={`Version ${versions.version2.version}`}
                size="small"
                sx={{
                  height: '24px',
                  fontSize: '12px',
                  backgroundColor: '#E8F5E9',
                  color: '#2E7D32',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#C8E6C9',
                  },
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      </HeaderContainer>

      {/* Content - This will be scrollable */}
      <Box>
        <Box
          sx={{
            backgroundColor: '#fff',
            height: 'calc(100vh - 120px)',
            borderRadius: '12px',
            border: `1px solid ${theme.palette.lightGray.main}`,
            padding: '24px',
            overflow: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {/* <Typography
            variant="h5"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 3,
            }}
          >
            Changes Between Versions
          </Typography> */}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {promptFields.map((field, index) => {
              const oldValue = versions.version1[field.key] || '';
              const newValue = versions.version2[field.key] || '';
              const hasChanged = isFieldDifferent(field.key);

              return (
                <FormControl fullWidth key={index}>
                  <FormLabel
                    htmlFor={`diff-field-${field.key}`}
                    sx={{
                      ...commonStyles.formLabel,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {field.label}
                    {hasChanged ? (
                      <Chip
                        label="Changed"
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '9px',
                          backgroundColor: '#FFF8E1',
                          color: '#F57F17',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Unchanged"
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '9px',
                          backgroundColor: theme.palette.custom.sidebarIconBg,
                          color: theme.palette.lightGray.text,
                        }}
                      />
                    )}
                  </FormLabel>

                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.lightGray.main}`,
                      borderRadius: '6px',
                      overflow: 'hidden',
                      mb: index === promptFields.length - 1 ? 2 : 0,
                      opacity: hasChanged ? 1 : 0.7,
                      '& .react-diff-viewer': {
                        fontFamily: 'DMSans',
                        fontSize: '14px',
                        border: 'none',
                      },
                      '& .react-diff-viewer table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        margin: 0,
                      },
                      '& .react-diff-viewer .diff-gutter': {
                        width: '50px',
                        minWidth: '50px',
                        padding: '8px 6px',
                        textAlign: 'center',
                        fontSize: '11px',
                        color: theme.palette.lightGray.text,
                        backgroundColor: theme.palette.custom.sidebarIconBg,
                        borderRight: `1px solid ${theme.palette.lightGray.main}`,
                        userSelect: 'none',
                        verticalAlign: 'top',
                      },
                      '& .react-diff-viewer .diff-code': {
                        padding: '8px 12px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        fontFamily: 'inherit',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        width: '100%',
                        verticalAlign: 'top',
                      },
                      '& .react-diff-viewer tr.line-removed': {
                        backgroundColor: '#FFEBEE !important',
                      },
                      '& .react-diff-viewer tr.line-removed .diff-gutter': {
                        backgroundColor: '#FFCDD2 !important',
                        color: '#C62828 !important',
                      },
                      '& .react-diff-viewer tr.line-removed .diff-code': {
                        backgroundColor: '#FFEBEE !important',
                      },
                      '& .react-diff-viewer tr.line-added': {
                        backgroundColor: '#E8F5E9 !important',
                      },
                      '& .react-diff-viewer tr.line-added .diff-gutter': {
                        backgroundColor: '#C8E6C9 !important',
                        color: '#2E7D32 !important',
                      },
                      '& .react-diff-viewer tr.line-added .diff-code': {
                        backgroundColor: '#E8F5E9 !important',
                      },
                      '& .react-diff-viewer tr.line-normal': {
                        backgroundColor: '#fff !important',
                      },
                      '& .react-diff-viewer tr.line-normal .diff-gutter': {
                        backgroundColor: `${theme.palette.custom.sidebarIconBg} !important`,
                        color: `${theme.palette.lightGray.text} !important`,
                      },
                      '& .react-diff-viewer tr.line-normal .diff-code': {
                        backgroundColor: '#fff !important',
                      },
                      '& .react-diff-viewer .word-removed': {
                        backgroundColor: '#FFCDD2 !important',
                        color: `${theme.palette.custom.textBlue} !important`,
                        padding: '1px 3px !important',
                        borderRadius: '2px !important',
                        textDecoration: 'none !important',
                      },
                      '& .react-diff-viewer .word-added': {
                        backgroundColor: '#C8E6C9 !important',
                        color: `${theme.palette.custom.textBlue} !important`,
                        padding: '1px 3px !important',
                        borderRadius: '2px !important',
                        textDecoration: 'none !important',
                      },
                      '& .react-diff-viewer .marker': {
                        fontSize: '11px',
                        fontWeight: 'bold',
                        width: '16px',
                        textAlign: 'center',
                        paddingRight: '4px',
                      },
                    }}
                  >
                    <ReactDiffViewer
                      oldValue={oldValue}
                      newValue={newValue}
                      splitView={false}
                      compareMethod={DiffMethod.WORDS}
                      hideLineNumbers={false}
                      showDiffOnly={false}
                      useDarkTheme={false}
                      extraLinesSurroundingDiff={1}
                      styles={{
                        variables: {
                          light: {
                            addedBackground: '#E8F5E9',
                            addedColor: theme.palette.custom.textBlue,
                            removedBackground: '#FFEBEE',
                            removedColor: theme.palette.custom.textBlue,
                            wordAddedBackground: '#C8E6C9',
                            wordRemovedBackground: '#FFCDD2',
                            addedGutterBackground: '#C8E6C9',
                            removedGutterBackground: '#FFCDD2',
                            gutterBackground:
                              theme.palette.custom.sidebarIconBg,
                            gutterBackgroundDark:
                              theme.palette.custom.sidebarIconBg,
                            highlightBackground:
                              theme.palette.custom.variableBg,
                            highlightGutterBackground: '#FFF4CC',
                          },
                        },
                        diffContainer: {
                          width: '100%',
                          border: 'none',
                        },
                        line: {
                          fontSize: '12px',
                          fontFamily: 'DMSans',
                          lineHeight: '1.5',
                        },
                        content: {
                          fontSize: '12px',
                          fontWeight: 400,
                          fontFamily: 'DMSans',
                        },
                        gutter: {
                          fontSize: '11px',
                          fontWeight: 400,
                          fontFamily: 'DMSans',
                          minWidth: '50px',
                          padding: '8px 6px',
                          textAlign: 'center',
                          userSelect: 'none',
                        },
                        marker: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          width: '16px',
                          textAlign: 'center',
                        },
                        wordDiff: {
                          padding: '1px 3px',
                          borderRadius: '2px',
                        },
                        emptyLine: {
                          backgroundColor: '#fff',
                        },
                      }}
                    />
                  </Box>
                </FormControl>
              );
            })}
          </Box>

          {promptFields.filter(field => isFieldDifferent(field.key)).length ===
            0 && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
                backgroundColor: theme.palette.custom.sidebarIconBg,
                color: theme.palette.custom.textBlue,
                '& .MuiAlert-icon': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              The selected versions are identical.
            </Alert>
          )}
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default PromptCompare;
